import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import LazyImage from '@/components/base/LazyImage';
import { blogPosts, blogCategories } from '@/mocks/blog';
import { useSEO } from '@/hooks/useSEO';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState<string>('Todos');

  useSEO({
    title: 'Blog | Dicas de Ergonomia, Escritório Corporativo e Tendências – Home Office Premium',
    description: 'Blog da Home Office Premium: artigos sobre ergonomia, design de escritórios corporativos, tendências em mobiliário, produtividade e sustentabilidade. Conteúdo especializado para empresas em Caruaru PE.',
    keywords: 'blog móveis corporativos, ergonomia escritório, tendências design corporativo, produtividade home office',
    path: '/blog',
    ogTitle: 'Blog | Home Office Premium – Ergonomia e Escritório Corporativo',
    ogDescription: 'Artigos especializados em ergonomia, design de escritórios, tendências e produtividade corporativa. Home Office Premium Caruaru PE.',
  });

  useEffect(() => {
    const schemaId = 'schema-blog';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Blog – Home Office Premium',
      description: 'Artigos especializados em ergonomia, design de escritórios corporativos, tendências em mobiliário e produtividade.',
      url: `${siteUrl}/blog`,
      publisher: { '@type': 'Organization', name: 'Home Office Premium', url: siteUrl },
    });

    const breadcrumbId = 'schema-blog-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      ],
    });

    const faqId = 'schema-blog-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'O que é ergonomia no trabalho e por que é importante?',
          acceptedAnswer: { '@type': 'Answer', text: 'Ergonomia é a ciência que adapta o ambiente de trabalho às necessidades do trabalhador. Um escritório ergonômico reduz dores, aumenta a produtividade e evita afastamentos. A NR-17 exige que empresas brasileiras atendam padrões mínimos de ergonomia.' },
        },
        {
          '@type': 'Question',
          name: 'Quais são as tendências de design de escritórios corporativos em 2025?',
          acceptedAnswer: { '@type': 'Answer', text: 'As principais tendências de 2025 incluem: open office com áreas de concentração (cabines acústicas), móveis modulares e flexíveis, biofilia (plantas e luz natural), paletas neutras com toques de cor e tecnologia integrada nas estações de trabalho.' },
        },
        {
          '@type': 'Question',
          name: 'Como melhorar a produtividade no escritório com móveis corporativos?',
          acceptedAnswer: { '@type': 'Answer', text: 'Investir em cadeiras ergonômicas certificadas NR-17, mesas na altura correta, divisórias para privacidade e cabines acústicas para concentração são as principais formas de aumentar a produtividade através do mobiliário corporativo.' },
        },
        {
          '@type': 'Question',
          name: 'A Home Office Premium tem conteúdo sobre móveis para escritórios em Caruaru e Pernambuco?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Nosso blog traz artigos baseados em projetos reais entregues em Caruaru, Recife, Petrolina, Garanhuns e todo Pernambuco. Abordamos ergonomia, NR-17, tendências, dicas de layout e cases de sucesso regionais.' },
        },
      ],
    });

    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  const filtered = activeCategory === 'Todos'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  const featured = blogPosts[0];
  const rest = filtered.filter(p => p.id !== featured.id);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <WhatsAppFloat />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-12 md:pb-16">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <span className="text-primary-600 text-[11px] font-semibold tracking-[0.2em] uppercase">
            Blog & Conteúdo
          </span>
          <h1 className="mt-4 text-[36px] md:text-[56px] font-semibold text-foreground-900 leading-[1.05] tracking-tight max-w-3xl">
            Ideias para espaços de trabalho melhores.
          </h1>
          <p className="mt-5 text-[15px] md:text-base text-foreground-500 leading-relaxed max-w-xl font-light">
            Conteúdo especializado em mobiliário corporativo, ergonomia, home office e design de interiores para empresas.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      {activeCategory === 'Todos' && (
        <section className="pb-16 md:pb-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-8">
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center cursor-pointer"
            >
              <div className="img-zoom rounded-2xl aspect-[16/10] bg-background-100">
                <LazyImage
                  src={featured.coverImage}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em]">
                    {featured.category}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-foreground-400 text-xs">Destaque</span>
                </div>
                <h2 className="text-[26px] md:text-[36px] font-semibold text-foreground-900 leading-[1.15] tracking-tight group-hover:text-primary-600 transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-[15px] text-foreground-500 leading-relaxed font-light max-w-lg">
                  {featured.excerpt}
                </p>
                <div className="mt-6 flex items-center gap-5 text-xs text-foreground-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-primary-600"></i>
                    {formatDate(featured.date)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-primary-600"></i>
                    {featured.readTime} min de leitura
                  </span>
                </div>
                <span className="mt-7 inline-flex items-center gap-2 text-primary-600 font-medium text-[15px] group-hover:gap-3 transition-all whitespace-nowrap">
                  Ler artigo completo
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Filter + Grid */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {['Todos', ...blogCategories].map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-foreground-900 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {(activeCategory === 'Todos' ? rest : filtered).map(post => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group cursor-pointer flex flex-col"
              >
                <div className="img-zoom rounded-2xl aspect-[16/10] bg-background-100">
                  <LazyImage
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="pt-5 flex flex-col flex-1">
                  <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em]">
                    {post.category}
                  </span>
                  <h3 className="mt-2 text-lg font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed font-light line-clamp-2 flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line"></i>
                      {post.readTime} min
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-foreground-400">
              <i className="ri-article-line text-4xl mb-3 block"></i>
              <p>Nenhum artigo encontrado nesta categoria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Ebooks CTA strip */}
      <section className="bg-foreground-950">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-600/20 rounded-xl flex-shrink-0">
              <i className="ri-book-open-line text-primary-500 text-2xl"></i>
            </div>
            <div>
              <p className="text-white font-semibold text-lg">Baixe nossos Ebooks Gratuitos</p>
              <p className="text-white/50 text-sm font-light mt-1">Guias práticos sobre NR-17, ergonomia e design de escritórios para Caruaru.</p>
            </div>
          </div>
          <Link
            to="/ebooks"
            className="inline-flex items-center gap-2 bg-primary-600 text-white font-medium px-7 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            Ver Ebooks
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
          <h2 className="text-[28px] md:text-[36px] font-semibold text-foreground-900 leading-[1.1] tracking-tight">
            Precisa de um projeto personalizado?
          </h2>
          <p className="mt-4 text-[15px] text-foreground-500 leading-relaxed font-light">
            Nossa equipe está pronta para transformar seu escritório. Solicite um orçamento sem compromisso.
          </p>
          <Link
            to="/contato"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium text-[15px] px-8 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Solicitar Orçamento
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}